import DirectionsWalkOutlinedIcon from "@mui/icons-material/DirectionsWalkOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { setCachedAnimationDetails } from "../../../app/slices/AnimationsSlice";
import PiButton from "../../../components/PiButton";
import { AnimationDetailsDTO } from "../../../dto/AnimationDetailsDTO";
import {
  MessageBubbleAnimation,
  noMessageBubbleAnimation,
  normalizeMessageBubbleAnimation,
} from "../../../models/interactions/MessageBubble";
import AnimationLibrary from "../animation/AnimationLibrary";
import {
  ANIMATION_UNAVAILABLE_LABEL,
  getAnimationDisplayLabel,
} from "../animation/animationDisplay";
import {
  useAnimationDetails,
  useAnimationRender,
} from "../animation/useAnimationAssets.hook";
import NodeInfoField from "../../sequence/components/NodeInfoField";
import "./MessageBubbleAnimationDropdown.css";

type MessageBubbleAnimationDropdownVariant =
  | "preview"
  | "node"
  | "conversation"
  | "conversation-inline"
  | "standalone";

export default function MessageBubbleAnimationDropdown(props: {
  selectedAnimation: MessageBubbleAnimation | string | undefined;
  onClick: (animation: MessageBubbleAnimation) => void;
  variant?: MessageBubbleAnimationDropdownVariant;
}) {
  const { selectedAnimation, onClick, variant = "preview" } = props;
  const normalizedAnimation = useMemo(
    () => normalizeMessageBubbleAnimation(selectedAnimation),
    [selectedAnimation]
  );
  const dispatch = useDispatch();
  const animationId = normalizedAnimation.animationId || undefined;
  const hasAnimationId = Boolean(animationId);
  const { details: animationDetails, isUnavailable } =
    useAnimationDetails(animationId);
  const { renderUri, isUnavailable: isRenderUnavailable } =
    useAnimationRender(animationId);
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [hasImageError, setHasImageError] = useState(false);

  useEffect(() => {
    setIsImageLoaded(false);
    setHasImageError(false);
  }, [renderUri]);

  const displayLabel = useMemo(
    () =>
      normalizedAnimation.animationState || normalizedAnimation.name
        ? getAnimationDisplayLabel(
            animationDetails ?? {
              name: normalizedAnimation.name,
              animationState: normalizedAnimation.animationState,
            }
          )
        : "No animation selected",
    [animationDetails, normalizedAnimation]
  );

  const handleSelectAnimation = (details: AnimationDetailsDTO) => {
    onClick({
      name: details.name,
      animationState: details.animationState,
      layer: details.layer,
      animationId: details.externalId,
    });
    dispatch(
      setCachedAnimationDetails({
        externalId: details.externalId,
        animationDetails: details,
      })
    );
  };

  const hasSelectedAnimation = Boolean(
    (normalizedAnimation.name || normalizedAnimation.animationState) &&
      !(
        !normalizedAnimation.name &&
        normalizedAnimation.animationState.trim().toLowerCase() === "none"
      )
  );
  const handleClearAnimation = () => {
    onClick({ ...noMessageBubbleAnimation });
  };

  const buttonText = hasSelectedAnimation ? "Change Animation" : "Select Animation";
  const isConversation = variant === "conversation";
  const isConversationInline = variant === "conversation-inline";
  const isPreview = variant === "preview";
  const isStandalone = variant === "standalone";
  const shouldShowPreview = Boolean(hasAnimationId && renderUri && !hasImageError);
  const shouldRenderStandalonePreviewFrame = isStandalone;
  const shouldRenderPreviewFrame =
    (shouldShowPreview && !isConversation) || shouldRenderStandalonePreviewFrame;
  const isPreviewUnavailable = hasImageError || isRenderUnavailable;
  const showLoadingState = Boolean(
    hasAnimationId && !renderUri && !isPreviewUnavailable && !isUnavailable
  );
  const showImageLoadingOverlay = Boolean(renderUri && !isImageLoaded && !hasImageError);

  const stopInlineInteraction = (event: { stopPropagation: () => void }) => {
    event.stopPropagation();
  };

  if (variant === "node") {
    return (
      <div
        className="flex w-full flex-col gap-[12px] noDrag nowheel"
        data-testid="bubble-animation-selector"
      >
        <NodeInfoField
          label="Animation"
          value={displayLabel}
          action={
            hasSelectedAnimation ? (
              <ClearAnimationButton onClear={handleClearAnimation} />
            ) : undefined
          }
        />
        <div
          className="relative flex h-[120px] w-full items-center justify-center overflow-hidden rounded-[5px] bg-interaction-softBackground"
          data-testid="animation-preview"
        >
          {renderUri && !hasImageError ? (
            <>
              {!isImageLoaded && (
                <div className="absolute inset-0 animate-pulse bg-creator-surface" />
              )}
              <img
                className="h-full w-full object-contain p-[8px]"
                src={renderUri}
                alt={displayLabel}
                onLoad={() => setIsImageLoaded(true)}
                onError={() => {
                  setHasImageError(true);
                  setIsImageLoaded(false);
                }}
              />
            </>
          ) : (
            <div className="flex items-center justify-center text-creator-text">
              <DirectionsWalkOutlinedIcon fontSize="large" color="inherit" />
            </div>
          )}
        </div>
        <div className="h-[36px]">
          <PiButton
            variant="piBlueOutlined"
            onClick={() => setIsLibraryOpen(true)}
            data-testid="select-animation"
          >
            {buttonText}
          </PiButton>
        </div>
        {isLibraryOpen && (
          <AnimationLibrary
            isOpen={isLibraryOpen}
            onClose={() => setIsLibraryOpen(false)}
            title="Animation Library"
            width="calc(90vw / var(--app-zoom, 1))"
            height="calc(90vh / var(--app-zoom, 1))"
            onSubmit={handleSelectAnimation}
          />
        )}
      </div>
    );
  }

  if (isConversation) {
    return (
      <div
        className="flex w-full flex-col noDrag nowheel"
        data-testid="bubble-animation-selector"
      >
        <div className="flex h-[38px] w-full items-center rounded-full border-[1.5px] border-solid border-interaction-primary bg-creator-surface text-interaction-primary transition-colors hover:bg-interaction-primary-washed">
          <button
            type="button"
            onClick={() => setIsLibraryOpen(true)}
            className="flex h-full min-w-0 flex-1 cursor-pointer items-center gap-[8px] border-0 bg-transparent pl-[12px] pr-[8px] text-left text-interaction-primary focus:outline-none"
            data-testid="select-animation"
          >
            <div className="flex shrink-0 items-center justify-center text-interaction-primary">
              <DirectionsWalkOutlinedIcon fontSize="small" color="inherit" />
            </div>
            <div
              className="min-w-0 flex-1 truncate text-[14px] font-medium text-interaction-primary"
              data-testid="selected-animation"
            >
              {displayLabel}
            </div>
          </button>
          {hasSelectedAnimation && (
            <ClearAnimationButton
              onClear={handleClearAnimation}
              className="mr-[8px]"
            />
          )}
        </div>
        {isLibraryOpen && (
          <AnimationLibrary
            isOpen={isLibraryOpen}
            onClose={() => setIsLibraryOpen(false)}
            title="Animation Library"
            width="calc(90vw / var(--app-zoom, 1))"
            height="calc(90vh / var(--app-zoom, 1))"
            onSubmit={handleSelectAnimation}
          />
        )}
      </div>
    );
  }

  if (isConversationInline) {
    return (
      <div
        className="noDrag nowheel flex min-w-0 max-w-[180px] items-center"
        data-testid="bubble-animation-selector"
        onClick={stopInlineInteraction}
        onPointerDown={stopInlineInteraction}
      >
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            setIsLibraryOpen(true);
          }}
          title={`Animation: ${displayLabel}`}
          className="inline-flex h-5 max-w-full cursor-pointer appearance-none items-center border-0 bg-transparent p-0 text-left text-[13px] font-medium leading-5 text-interaction-primary underline hover:text-interaction-secondary focus:outline-none"
          data-testid="select-animation"
        >
          <DirectionsWalkOutlinedIcon
            aria-hidden="true"
            fontSize="inherit"
            className="mr-[4px] shrink-0 text-[16px]"
          />
          <span className="block truncate" data-testid="selected-animation">
            {displayLabel}
          </span>
        </button>
        {hasSelectedAnimation && (
          <ClearAnimationButton
            onClear={handleClearAnimation}
            className="ml-[4px]"
          />
        )}
        {isLibraryOpen && (
          <AnimationLibrary
            isOpen={isLibraryOpen}
            onClose={() => setIsLibraryOpen(false)}
            title="Animation Library"
            width="calc(90vw / var(--app-zoom, 1))"
            height="calc(90vh / var(--app-zoom, 1))"
            onSubmit={handleSelectAnimation}
          />
        )}
      </div>
    );
  }

  if (isStandalone && !hasSelectedAnimation) {
    return (
      <div
        className="messagebubble-animation-picker-standalone noDrag nowheel items-center justify-center"
        data-testid="bubble-animation-selector"
      >
        <div
          className="flex items-center gap-[6px] text-[13px] font-medium text-interaction-primary"
          role="status"
          data-testid="no-animation-selected"
        >
          <DirectionsWalkOutlinedIcon
            aria-hidden="true"
            fontSize="inherit"
            className="text-[20px]"
          />
          <span>No animation selected</span>
        </div>
        <div className="h-[36px] w-full max-w-[180px]">
          <PiButton
            variant="piBlueOutlined"
            onClick={() => setIsLibraryOpen(true)}
            data-testid="select-animation"
          >
            Select Animation
          </PiButton>
        </div>
        {isLibraryOpen && (
          <AnimationLibrary
            isOpen={isLibraryOpen}
            onClose={() => setIsLibraryOpen(false)}
            title="Animation Library"
            width="calc(90vw / var(--app-zoom, 1))"
            height="calc(90vh / var(--app-zoom, 1))"
            onSubmit={handleSelectAnimation}
          />
        )}
      </div>
    );
  }

  return (
    <div
      className={
        isStandalone
          ? "messagebubble-animation-picker-standalone noDrag nowheel"
          : isPreview
          ? `messagebubble-animation-picker-preview ${
              shouldShowPreview ? "" : "messagebubble-animation-picker-preview--noPreview"
            } noDrag nowheel`
          : isConversation
          ? "flex h-full w-full flex-col gap-[6px]"
          : "flex w-full flex-col gap-[8px] noDrag nowheel"
      }
      data-testid="bubble-animation-selector"
    >
      {shouldRenderPreviewFrame && (
        <div
          className={
            isStandalone
              ? "messagebubble-animation-picker-standaloneFrame"
              : isPreview
              ? "messagebubble-animation-picker-previewFrame"
              : isConversation
              ? "relative flex h-[56px] w-full items-center justify-center overflow-hidden rounded-[5px] bg-interaction-text-hoverBackground"
              : "relative flex h-[96px] w-full items-center justify-center overflow-hidden rounded-[5px] bg-interaction-softBackground"
          }
          data-testid="animation-preview"
        >
          {(showLoadingState || showImageLoadingOverlay) && (
            <div className="absolute inset-0 animate-pulse bg-creator-surface" />
          )}
          {isStandalone && !hasAnimationId && (
            <div className="messagebubble-animation-picker-standaloneState">
              Select an animation to preview it here.
            </div>
          )}
          {isStandalone && hasAnimationId && isUnavailable && (
            <div
              className="messagebubble-animation-picker-standaloneState"
              data-testid="animation-unavailable"
            >
              {ANIMATION_UNAVAILABLE_LABEL}
            </div>
          )}
          {isStandalone && hasAnimationId && !isUnavailable && isPreviewUnavailable && (
            <div className="messagebubble-animation-picker-standaloneState">
              Preview unavailable for this animation.
            </div>
          )}
          {renderUri && !hasImageError && (
            <img
              className="h-full w-full object-contain p-[8px]"
              src={renderUri}
              alt={displayLabel}
              onLoad={() => setIsImageLoaded(true)}
              onError={() => {
                setHasImageError(true);
                setIsImageLoaded(false);
              }}
            />
          )}
        </div>
      )}
      <div
        className={
          isStandalone
            ? "messagebubble-animation-picker-standaloneFields"
            : isPreview
            ? "messagebubble-animation-picker-fields"
            : ""
        }
      >
        <div
          className={
            isConversation
              ? "flex h-[32px] w-full items-center gap-[8px] truncate rounded-[5px] bg-interaction-text-hoverBackground px-[8px] text-interaction-text-primary"
              : isStandalone
              ? "messagebubble-animation-picker-standaloneSelected"
              : "flex min-h-[40px] w-full items-center gap-[8px] rounded-[10px] border border-solid px-[12px]"
          }
          style={
            isPreview
              ? {
                  borderColor: "var(--pi-game-blue-faded)",
                  backgroundColor: "var(--pi-surface)",
                }
              : undefined
          }
          data-testid="selected-animation"
        >
          {(isConversation || isPreview) && (
            <div className="flex shrink-0 items-center justify-center text-[20px] text-interaction-primary">
              <DirectionsWalkOutlinedIcon fontSize="inherit" color="inherit" />
            </div>
          )}
          <div
            className={
              isStandalone
                ? "min-w-0 flex-1 truncate text-[14px] font-medium text-interaction-primary"
                : isPreview
                ? "min-w-0 flex-1 truncate text-[14px] font-medium text-interaction-primary"
                : "min-w-0 flex-1 truncate text-[12px]"
            }
          >
            {displayLabel}
          </div>
          {hasSelectedAnimation && (
            <ClearAnimationButton onClear={handleClearAnimation} />
          )}
        </div>
        <div className="h-[36px]">
          <PiButton
            variant="piBlueOutlined"
            onClick={() => setIsLibraryOpen(true)}
            data-testid="select-animation"
          >
            {buttonText}
          </PiButton>
        </div>
      </div>
      {isLibraryOpen && (
        <AnimationLibrary
          isOpen={isLibraryOpen}
          onClose={() => setIsLibraryOpen(false)}
          title="Animation Library"
          width="calc(90vw / var(--app-zoom, 1))"
          height="calc(90vh / var(--app-zoom, 1))"
          onSubmit={handleSelectAnimation}
        />
      )}
    </div>
  );
}

function ClearAnimationButton(props: {
  onClear: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      aria-label="Clear animation"
      title="Clear animation"
      className={`flex h-[24px] w-[24px] shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent p-0 text-[18px] text-interaction-primary transition-colors hover:bg-interaction-primary-washed focus:outline-none focus-visible:ring-2 focus-visible:ring-interaction-primary ${
        props.className ?? ""
      }`}
      onClick={(event) => {
        event.stopPropagation();
        props.onClear();
      }}
      onPointerDown={(event) => event.stopPropagation()}
      data-testid="clear-animation"
    >
      <CloseIcon fontSize="inherit" color="inherit" />
    </button>
  );
}
